"use client";

import { CitationTool } from "@/components/citation-tool";
import Link from "@/components/locale-link";
import { useToolUi } from "@/components/tools/tool-ui";
import { getFeaturedStyles } from "@/data/citation-styles";

/** English strings. A translated content file overrides these through `ui`. */
export const ANTHOLOGY_CITATION_GENERATOR_STRINGS = {
  howToCiteAnAnthology: "How to Cite an Anthology",
  n1DecideWhatYouAre: "1. Decide what you are citing.",
  citingTheWholeEditedCollection:
    "Citing the whole edited collection? The editors go in the author field — APA renders them with",
  eds: "(Eds.)",
  mlaWithEditorsCitingOne:
    ', MLA with "editors". Citing one story, poem, or essay inside it? Use the',
  bookChapterCitationGenerator: "book chapter citation generator",
  insteadThatWorkGetsIts:
    "instead — that work gets its own author, title, and page range.",
  n2EnterTheCollectionDetails:
    "2. Enter the collection details and pick a style.",
  addTheAnthologySTitle:
    "Add the anthology's title, edition, publisher, and year, then choose APA, MLA, Chicago, or any of the {styleCount} styles. The editor labels and punctuation switch automatically with the style.",
  n3Generate: "3. Generate.",
  weBuildAFullEdited:
    " We build a full edited-collection reference with the editors credited correctly. Edit any field, then copy or export it to BibTeX, RIS, CSV, or Word.",
} as const;

/**
 * Live citation generator embedded on the Anthology landing page. This page cites the
 * WHOLE edited collection (editors in the author position), so the tool opens on the
 * manual-entry tab preset to the CSL `collection` type. A single work inside the
 * anthology is a chapter — the page links to the book-chapter tool for that case.
 */
function AnthologyGenerator() {
  return (
    <CitationTool
      initialManualType="collection"
      initialMode="manual"
      styles={getFeaturedStyles()}
    />
  );
}

export default function AnthologyCitationGeneratorWidget() {
  const strings = useToolUi(ANTHOLOGY_CITATION_GENERATOR_STRINGS);

  return (
    <>
      {/* Live generator (manual entry preset to the Anthology / collection source type) */}
      <AnthologyGenerator />
      {/* How to */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.howToCiteAnAnthology}
        </h2>
        <ol className="space-y-3 text-muted-foreground text-sm leading-relaxed">
          <li>
            <strong className="text-foreground">
              {strings.n1DecideWhatYouAre}
            </strong>{" "}
            {strings.citingTheWholeEditedCollection}{" "}
            <span className="font-medium text-foreground">{strings.eds}</span>
            {strings.mlaWithEditorsCitingOne}{" "}
            <Link
              className="text-foreground underline underline-offset-4"
              href="/tools/book-chapter-citation-generator"
            >
              {strings.bookChapterCitationGenerator}
            </Link>{" "}
            {strings.insteadThatWorkGetsIts}
          </li>
          <li>
            <strong className="text-foreground">
              {strings.n2EnterTheCollectionDetails}
            </strong>{" "}
            {strings.addTheAnthologySTitle}
          </li>
          <li>
            <strong className="text-foreground">{strings.n3Generate}</strong>
            {strings.weBuildAFullEdited}
          </li>
        </ol>
      </section>
    </>
  );
}

/**
 * English only for now. Add locales here as this page type is translated — the
 * registry in src/lib/i18n/localized-routes.ts has to agree, or the proxy will
 * redirect the URL this generates away again.
 */
export function generateStaticParams() {
  return [{ locale: "en" }];
}

export const dynamicParams = false;
